import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { groupBy, isEqual } from 'lodash';
import numbro from 'numbro';
import { shallow } from 'zustand/shallow';

import { LoadingSpinner } from '../../components/Molecules';
import { ConfirmReferralPopup } from '../../components/Popups/ConfirmReferralPopup';
import {
  SelectVersionState,
  WebStoreProductSelectVersionTemplate,
} from '../../components/Templates';
import { RegionsCode } from '../../constants/region';
import { AppToastErrorOption } from '../../constants/toast';
import { usePopup, useShareData } from '../../context';
import useDoWhile, { DoWhileFn } from '../../hooks/useDoWhile';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useLocale from '../../hooks/useLocale';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import { AttachmentImage } from '../../models/Base';
import {
  ReferralSystem,
  RewardOfferPrice,
  RewardOfferType,
  RewardRedemptionState,
  VariantsData,
} from '../../models/RewardStore';
import { SpaceMembershipTierSubscriptionListType } from '../../models/SpaceStore';
import { Profile, ResetPasswordResult } from '../../models/UserStore';
import {
  useRewardStore,
  useRewardStoreListField,
} from '../../stores/RewardStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';
import { getLocalCurrencyByRegion } from '../../utils/region';

interface VariantInfoWithName {
  groupName: string;
  groupIndex: number;
  name: string;
  description: string;
  variant: Record<string, string>;
}

type State = {
  loadingTotal: number;
  finishFetchOffer: boolean;
  referralCode: string | undefined;
  lastReferralCode: string | undefined;
  referredByProfile: Profile | null | undefined;
  isValid: boolean;
  isLoading: boolean;
  rewardRedemptionId: string | undefined;
  hasCancelled: boolean;
  isNeedFetchAgain: boolean;
  selectedVariant: Record<string, string> | undefined;
  isEmptyPurchase: boolean;
};

const {
  fetchOffer,
  fetchRewardReferral,
  createRedemption,
  fetchRedemption,
  cancelRedemption,
  referralCodeValidation,
} = useRewardStore.getState();

const WebStoreProductSelectVersionScreen = () => {
  const l = useLocale();
  const { t } = useTranslation();
  const location = useLocation();
  const { sharedData, setSharedData } = useShareData();
  const { showPopup, hidePopup } = usePopup();

  const { rewardOfferId } = useParams();
  const activeRegion = useSessionStore((store) => store.activeRegion);

  const userId = useSessionStore((store) => store.activeUserId);

  const userProfile = useUserStore((store) => store.getProfile(userId ?? ''));

  const query = new URLSearchParams(location.search);

  const redemptionId = query.get('rewardRedemptionId');
  const referralCode = query.get('referralCode');

  const currency = useMemo(() => {
    return userProfile?.localCurrency ?? getLocalCurrencyByRegion(activeRegion);
  }, [activeRegion, userProfile?.localCurrency]);

  const [state, setState] = useState<State>({
    loadingTotal: 0,
    finishFetchOffer: false,
    referralCode: undefined,
    lastReferralCode: undefined,
    referredByProfile: undefined,
    isValid: false,
    isLoading: false,
    rewardRedemptionId: undefined,
    hasCancelled: false,
    isNeedFetchAgain: false,
    selectedVariant: undefined,
    isEmptyPurchase: false,
  });

  useRedirectEntry({ allowTrial: false });

  useEffect(() => {
    if (userId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchRewardReferral({ referralSystem: ReferralSystem.Joyajoy })
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
        });
    }
  }, [userId]);

  const rewardReferral = useRewardStore(
    (store) => store.getRewardReferral(`${ReferralSystem.Joyajoy}_${userId}`),
    shallow,
  );

  useEffect(() => {
    const code = rewardReferral?.rewardReferralCode?.referralCode;
    if (code) {
      setState((s) => ({
        ...s,
        referralCode: code,
      }));
    }
  }, [referralCode, rewardReferral?.rewardReferralCode?.referralCode]);

  const rewardOfferStore = 'system';

  useRewardStoreListField('offers', `${rewardOfferStore}_${rewardOfferId}`);

  useEffect(() => {
    if (rewardOfferId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchOffer(rewardOfferStore, rewardOfferId)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
            finishFetchOffer: true,
          }));
        });
    }
  }, [rewardOfferId]);

  const rewardOffer = useRewardStore(
    (store) => store.getOffer(rewardOfferId ?? ''),
    shallow,
  );

  useRedirectHome(state.finishFetchOffer && !rewardOffer);

  const checkReferralCode = useCallback(async () => {
    if (state.referralCode === state.lastReferralCode) {
      return;
    }

    if (state.referralCode) {
      const res = await referralCodeValidation(
        { referralSystem: ReferralSystem.Joyajoy },
        { referralCode: state.referralCode },
      );
      setState((s) => ({
        ...s,
        lastReferralCode: state.referralCode,
      }));
      if (res === ResetPasswordResult.Success) {
        setState((s) => ({
          ...s,
          isValid: true,
        }));
        setSharedData({
          ...sharedData,
          referralCode:
            rewardOffer?.reward.type === RewardOfferType.ReferralRole
              ? ''
              : state.referralCode,
        });
      } else if (res === ResetPasswordResult.Error) {
        setState((s) => ({
          ...s,
          isValid: false,
        }));
        setSharedData({
          ...sharedData,
          referralCode: '',
          searchReferralCode: '',
        });
      } else {
        setState((s) => ({
          ...s,
          referredByProfile: res,
          isValid: true,
        }));
        setSharedData({
          ...sharedData,
          referralCode:
            rewardOffer?.reward.type === RewardOfferType.ReferralRole
              ? ''
              : state.referralCode,
        });
      }
    } else {
      setState((s) => ({ ...s, isValid: false }));
    }
  }, [
    state.referralCode,
    state.lastReferralCode,
    setSharedData,
    sharedData,
    rewardOffer?.reward.type,
  ]);

  useEffect(() => {
    if (state.referralCode) {
      checkReferralCode().catch(() => {});
    }
  }, [checkReferralCode, state.referralCode]);

  const rewardRedemptionsFieldName = `${rewardOfferStore}_${rewardOfferId}`;
  const rewardRedemptionsField = useRewardStoreListField(
    'redemptions',
    rewardRedemptionsFieldName,
  );

  const fetchRewardRedemptions = useCallback<FetchListFn>(
    (cursor) =>
      useRewardStore
        .getState()
        .fetchRedemptionsByOfferId(
          rewardOfferStore,
          rewardOfferId ?? '',
          cursor,
        ),
    [rewardOfferId],
  );

  const { fetchAll: fetchAllRewardRedemptions } = useFetchList(
    rewardRedemptionsField,
    fetchRewardRedemptions,
  );

  useEffect(() => {
    if ((userId && rewardOfferId) || state.isNeedFetchAgain) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchAllRewardRedemptions()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
            isNeedFetchAgain: false,
          }));
        });
    }
  }, [
    fetchAllRewardRedemptions,
    rewardOfferId,
    state.isNeedFetchAgain,
    userId,
  ]);

  const rewardRedemptions = useRewardStore((rewardState) => {
    return rewardState.getRedemptions(rewardRedemptionsFieldName);
  }, arrayEqual(shallow));

  const payingRedemption = useMemo(() => {
    return rewardRedemptions.find(
      (item) => item.state === RewardRedemptionState.Paying,
    );
  }, [rewardRedemptions]);

  useEffect(() => {
    if (
      (redemptionId !== null ||
        payingRedemption?.rewardRedemptionId !== undefined) &&
      !state.hasCancelled
    ) {
      const idToCancel: string =
        redemptionId || payingRedemption?.rewardRedemptionId || '';

      cancelRedemption(rewardOfferStore, idToCancel)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, hasCancelled: true }));
        });
    }
  }, [payingRedemption?.rewardRedemptionId, redemptionId, state.hasCancelled]);

  const loadRedemption = useDoWhile(
    useCallback<DoWhileFn>(
      async (signal) => {
        if (state.rewardRedemptionId !== undefined) {
          const res = await fetchRedemption(
            rewardOfferStore,
            state.rewardRedemptionId,
            { signal },
          );
          if (res) {
            switch (res.state) {
              case RewardRedemptionState.Paying:
                {
                  const url = res.payment?.url;
                  if (url !== undefined) {
                    setState((s) => ({ ...s, isLoading: false }));
                    window.location.href = url;
                  }
                }
                return true;
              case RewardRedemptionState.Fulfilled:
                return true;
              case RewardRedemptionState.Failed:
                return false;
            }
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 3000));
        return false;
      },
      [state.rewardRedemptionId],
    ),
  );

  useEffect(() => {
    if (state.rewardRedemptionId !== undefined) {
      loadRedemption().catch(() => {});
    }
  }, [loadRedemption, state.rewardRedemptionId]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        const url = new URL(window.location.href);
        if (url.search) {
          const queryParams = new URLSearchParams(window.location.search);
          queryParams.delete('rewardRedemptionId');

          url.search = queryParams.toString();
          window.history.replaceState(null, '', url.toString());
          window.location.reload(); // Reload the page when navigating back
        } else {
          window.location.reload(); // Reload the page when navigating back
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const spaceMembershipTierSubscriptionFieldName =
    SpaceMembershipTierSubscriptionListType.ActiveUser;
  const spaceMembershipTierSubscriptionField = useSpaceStoreListField(
    'spaceMembershipTierSubscriptions',
    spaceMembershipTierSubscriptionFieldName,
  );
  const fetchTierSubscriptions = useCallback<FetchListFn>(
    (cursor) => useSpaceStore.getState().fetchTierSubscriptions(cursor),
    [],
  );
  const { fetchAll: fetchAllTierSubscriptions } = useFetchList(
    spaceMembershipTierSubscriptionField,
    fetchTierSubscriptions,
  );

  useEffect(() => {
    if (userId && rewardOfferId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchAllTierSubscriptions()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
          }));
        });
    }
  }, [fetchAllTierSubscriptions, rewardOfferId, userId]);

  const spaceMembershipTierSubscriptions = useSpaceStore((store) => {
    return store.getTierSubscriptions(spaceMembershipTierSubscriptionFieldName);
  }, arrayEqual(shallow));

  const activatedTierSubscriptions = useMemo(() => {
    return spaceMembershipTierSubscriptions.filter(
      (subscriptions) => subscriptions.isActivated,
    );
  }, [spaceMembershipTierSubscriptions]);

  const i18nRewardOfferInfo = l(rewardOffer?.i18nRewardOfferInfo);

  const carousel = useMemo(() => {
    return (
      i18nRewardOfferInfo?.carousel
        .map((item) => item.image)
        .filter((image): image is AttachmentImage => image !== undefined)
        .map((image) => ({
          resource: { uri: image.publicUrls?.large ?? '' },
        })) ?? []
    );
  }, [i18nRewardOfferInfo?.carousel]);

  const getPrices = useCallback(
    (
      priceList: RewardOfferPrice,
      isReferralPrice: boolean,
    ): string | undefined => {
      const isValid = state.isValid;

      let c = currency;
      let priceNumber =
        isReferralPrice && isValid
          ? priceList?.[`referral${currency}`]
          : priceList?.[`${currency}`];
      if (priceNumber === undefined) {
        c = 'HKD';
        priceNumber =
          (isReferralPrice && isValid
            ? priceList?.[`referralHKD`]
            : priceList?.[`HKD`]) ?? 0;
      }

      if (!isValid && !isReferralPrice) {
        return undefined;
      }

      return `${c}${numbro(priceNumber / 100).format({
        thousandSeparated: true,
        mantissa: 2,
      })}`;
    },
    [currency, state.isValid],
  );

  const onClickPurchase = useCallback(async () => {
    if (!rewardOfferId) {
      toast(t('Product not found'), AppToastErrorOption);
      return;
    }

    if (!state.selectedVariant) {
      setState((s) => ({ ...s, isEmptyPurchase: true }));
      return;
    }

    if (state.referredByProfile) {
      const confirmed = await new Promise((resolve) => {
        showPopup(
          <ConfirmReferralPopup
            onPrimary={() => {
              hidePopup();
              resolve(true);
            }}
            onCancel={() => {
              hidePopup();
              resolve(false);
            }}
            profile={state.referredByProfile!}
          />,
        );
      });

      if (!confirmed) return;
    }

    setState((s) => ({ ...s, isLoading: true }));

    let price = rewardOffer?.price;
    if (
      rewardOffer?.reward &&
      rewardOffer?.reward.type === RewardOfferType.ClassroomMembershipTierSet
    ) {
      price =
        rewardOffer?.reward.items.find((item) =>
          isEqual(item.variant, state.selectedVariant),
        )?.price ?? price;
    }

    createRedemption(rewardOfferStore, {
      rewardOfferId: rewardOfferId,
      quantity: 1,
      paymentGateway:
        activeRegion === RegionsCode.CN ||
        (rewardOffer?.reward.type === RewardOfferType.WebPlan &&
          state.selectedVariant.period === 'monthly')
          ? 'stripe'
          : 'paypal',
      referralCode: state.isValid ? state.referralCode : undefined,
      variant: state.selectedVariant,
      currency: getPrices(price ?? {}, false)?.slice(0, 3),
    })
      .then((res) => {
        if (res) {
          setState((s) => ({
            ...s,
            rewardRedemptionId: res.rewardRedemptionId,
          }));
        } else {
          setState((s) => ({ ...s, isLoading: false }));
          toast(
            t('Failed to create order. Please refresh the page and try again.'),
            AppToastErrorOption,
          );
          setState((s) => ({ ...s, isNeedFetchAgain: true }));
        }
      })
      .catch(() => {
        setState((s) => ({ ...s, isLoading: false }));
        toast(
          t('Failed to create order. Please refresh the page and try again.'),
          AppToastErrorOption,
        );
        setState((s) => ({ ...s, isNeedFetchAgain: true }));
      });
  }, [
    activeRegion,
    getPrices,
    hidePopup,
    rewardOffer?.price,
    rewardOffer?.reward,
    rewardOfferId,
    showPopup,
    state.isValid,
    state.referralCode,
    state.referredByProfile,
    state.selectedVariant,
    t,
  ]);

  const generateVariants = useCallback((infos: VariantsData[]) => {
    const variants: VariantInfoWithName[] = [];

    const generateCombinations = (
      index: number,
      currentVariant: Record<string, string>,
      groupIndex: number | undefined,
      groupName: string | undefined,
    ) => {
      if (index === infos.length) {
        const variantName = infos
          .map((info) => {
            const value = currentVariant[info.key];
            const option = info.options.find((o) => o.value === value);
            return option?.name;
          })
          .join(' - ');
        const variantDescription = infos
          .map((info) => {
            const value = currentVariant[info.key];
            const option = info.options.find((o) => o.value === value);
            return option?.description;
          })
          .join('\n');
        const infoWithName: VariantInfoWithName = {
          name: variantName,
          description: variantDescription,
          variant: { ...currentVariant },
          groupName: groupName ?? '',
          groupIndex: groupIndex ?? -1,
        };
        variants.push(infoWithName);
        return;
      }

      infos[index]?.options.forEach((option, optionIndex) => {
        const aaa = infos[index]?.key;
        if (aaa) {
          generateCombinations(
            index + 1,
            {
              ...currentVariant,
              [aaa]: option.value,
            },
            groupIndex ?? optionIndex,
            groupName ?? infos[index]?.groupName,
          );
        }
      });
    };

    generateCombinations(0, {}, undefined, undefined);

    const groupVariants = Object.values(groupBy(variants, 'groupIndex'));
    if (groupVariants.every((variant) => variant.length === 1)) {
      return [variants];
    } else {
      return groupVariants;
    }
  }, []);

  const versions = useMemo(() => {
    if (
      rewardOffer &&
      rewardOffer?.reward.type === RewardOfferType.ClassroomMembershipTierSet
    ) {
      const variantInfos = l(rewardOffer.reward.i18nVariantsData);
      const variantItems = rewardOffer.reward.items;

      const variantInfoWithNames = variantInfos
        ? generateVariants(variantInfos)
        : [];

      return variantInfoWithNames.map((variantInfoWithName) => {
        const hasCompletedPurchase = variantInfoWithName.every(
          (variantInfo) => {
            const variantItem = variantItems.find((item) =>
              isEqual(item.variant, variantInfo.variant),
            );
            const tierSubscription = activatedTierSubscriptions.find(
              (subscription) =>
                subscription.tierId === variantItem?.tierId &&
                subscription.classroomId === variantItem.classroomId,
            );
            return tierSubscription;
          },
        );

        return {
          hasError: state.isEmptyPurchase && !hasCompletedPurchase,
          groupName: variantInfoWithName[0]?.groupName ?? '',
          data: variantInfoWithName.map((variantInfo) => {
            const variantItem = variantItems.find((item) =>
              isEqual(item.variant, variantInfo.variant),
            );
            const tierSubscription = activatedTierSubscriptions.find(
              (subscription) =>
                subscription.tierId === variantItem?.tierId &&
                subscription.classroomId === variantItem.classroomId,
            );
            const completedRedemption = rewardRedemptions.find(
              (redemption) =>
                redemption.isCompleted() &&
                isEqual(redemption.variant, variantInfo.variant),
            );
            return {
              value: variantInfo.variant,
              title: variantInfo.name,
              description: variantInfo.description,
              price: variantItem?.price
                ? getPrices(variantItem?.price, true)
                : undefined,
              originalPrice: variantItem?.price
                ? getPrices(variantItem?.price, false)
                : undefined,
              selected: isEqual(state.selectedVariant, variantInfo.variant),
              state: completedRedemption
                ? SelectVersionState.Purchased
                : tierSubscription
                ? SelectVersionState.Disabled
                : SelectVersionState.Available,
            };
          }),
        };
      });
    } else if (
      rewardOffer &&
      rewardOffer?.reward.type === RewardOfferType.WebPlan
    ) {
      const variantItems = rewardOffer.reward.items;
      return [
        {
          hasError: false,
          groupName: t('Subscription Option'),
          data: variantItems.map((variantItem) => {
            const info = {
              monthly: {
                title: 'Monthly Subscription',
                description: 'Per Month',
              },
              yearly: {
                title: 'Yearly Subscription',
                description: 'Per Year',
              },
            };
            const { title, description } = info[variantItem.variant.period];

            // handle only tier1 now
            return {
              value: variantItem.variant,
              title: t(title),
              description: t(description),
              price: variantItem?.price
                ? getPrices(variantItem?.price, true)
                : undefined,
              originalPrice: variantItem?.price
                ? getPrices(variantItem?.price, false)
                : undefined,
              selected: isEqual(state.selectedVariant, variantItem.variant),
              state: SelectVersionState.Available,
            };
          }),
        },
      ];
    }
    return [];
  }, [
    activatedTierSubscriptions,
    generateVariants,
    getPrices,
    l,
    rewardOffer,
    rewardRedemptions,
    state.isEmptyPurchase,
    state.selectedVariant,
    t,
  ]);

  const onClickItem = useCallback((value: Record<string, string>) => {
    setState((s) => ({ ...s, selectedVariant: value, isEmptyPurchase: false }));
  }, []);

  useEffect(() => {
    if (!state.selectedVariant && versions.length > 0) {
      const firstAvailableGroup = versions.find((group) =>
        group.data.some((item) => item.state === SelectVersionState.Available),
      );

      if (firstAvailableGroup) {
        const firstAvailableItem = firstAvailableGroup.data.find(
          (item) => item.state === SelectVersionState.Available,
        );

        if (firstAvailableItem) {
          setState((s) => ({
            ...s,
            selectedVariant: firstAvailableItem.value,
          }));
        }
      }
    }
  }, [versions, state.selectedVariant]);
  return (
    <>
      {state.loadingTotal || state.isLoading ? (
        <LoadingSpinner loading={!!state.loadingTotal || state.isLoading} />
      ) : null}
      <WebStoreProductSelectVersionTemplate
        name={i18nRewardOfferInfo?.name}
        carousel={carousel}
        versions={versions}
        onClickItem={onClickItem}
        onClickPurchase={() => {
          onClickPurchase().catch(console.error);
        }}
      />
    </>
  );
};

export default WebStoreProductSelectVersionScreen;
